import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import API, { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '/images/page-banners/admission-guidliness.jpg') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const DEFAULT_ENGAGEMENTS = [
  {
    title: 'Plantation & Environmental Awareness',
    subtitle: 'Nurturing Nature. Growing Responsibility.',
    text: 'Plantation activities help children experience the responsibility of caring for a living world. From planting saplings to observing their growth, students explore the connection between nature, human choices and environmental well-being. These experiences cultivate patience, awareness and a sense of stewardship, encouraging children to translate concern for the planet into thoughtful everyday actions.',
    icon: 'fa-seedling',
    image: '/images/engagement/peer-to-peer.webp',
  },
  {
    title: 'Presentations & Public Speaking',
    subtitle: 'Empowering Every Voice with Clarity and Confidence',
    text: 'An idea gains strength when a child can express it with conviction. Presentations, show-and-tell activities and classroom discussions encourage students to organise their thoughts, communicate clearly and respond thoughtfully to questions. With supportive guidance, they learn to speak with confidence, listen with respect and appreciate perspectives beyond their own.',
    icon: 'fa-microphone',
    image: '/images/engagement/Presentations.webp',
  },
  {
    title: 'Art, Storytelling & Creative Expression',
    subtitle: 'Giving Imagination a Meaningful Voice',
    text: 'Creative expression allows children to communicate what they see, think and feel. Art, storytelling, role play, music and performance create opportunities to explore different perspectives and develop an individual voice. Through these experiences, students strengthen their imagination, emotional awareness and confidence in sharing original work.',
    icon: 'fa-palette',
    image: '/images/engagement/Storytelling.webp',
  },
  {
    title: 'Teamwork & Student Leadership',
    subtitle: 'Taking Initiative. Sharing Responsibility.',
    text: 'Leadership develops through purposeful action and consideration for others. Collaborative tasks and group projects encourage students to contribute ideas, share responsibilities and work through differences respectfully. Children learn that effective leadership involves listening, honouring commitments and helping others succeed alongside them.',
    icon: 'fa-users',
    image: '/images/engagement/teamwork.webp',
  },
  {
    title: 'Sports & Collaborative Play',
    subtitle: 'Building Resilience Through Every Challenge',
    text: 'Sport gives children opportunities to practise discipline, make decisions and contribute to a team. Through games and collaborative play, students learn to compete fairly, respond constructively to setbacks and recognise the value of consistent effort. These experiences strengthen resilience and encourage a healthy, positive approach to participation.',
    icon: 'fa-futbol',
    image: '/images/engagement/sports.webp',
  },
  {
    title: 'Peer Connections & Reflection',
    subtitle: 'Creating a Culture of Belonging and Growth',
    text: "Children participate more confidently when they feel heard, respected and supported. Meaningful classroom conversations, peer interaction and opportunities for reflection help students recognise their progress and appreciate one another's contributions. Caring teacher–student relationships strengthen this sense of belonging, encouraging children to ask for help, share ideas and approach learning with confidence.",
    icon: 'fa-handshake',
    image: '/images/engagement/20250422_085453.webp',
  },
];

const BeyondClassroomPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/pages/public/beyond-classroom')
      .then((res) => {
        if (res.data?.data) {
          setPageData(res.data.data);
        }
      })
      .catch(() => {
        API.get('/pages/public/student-engagement')
          .then((res) => {
            if (res.data?.data) {
              setPageData(res.data.data);
            }
          })
          .catch(() => {});
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const sections = pageData?.sections || {};

  // Banner
  const banner = sections.banner || pageData?.banner || {};
  const bannerTitle = banner.title || 'Learning comes alive when every child participates.';
  const bannerSubtitle = banner.subtitle || 'Beyond Classroom';
  const bannerBreadcrumb = banner.breadcrumb || 'BEYOND CLASSROOM';
  const bannerImgSrc = pageData?.banner_image
    ? resolveImage(pageData.banner_image)
    : '/images/page-banners/admission-guidliness.jpg';
  const bannerLead =
    banner.lead ||
    'We encourage students to ask questions, explore ideas and discover their potential through active participation and hands-on learning experiences in Sonipat, Haryana.';

  // Philosophy
  const philosophy = sections.philosophy || {};
  const philKicker = philosophy.kicker || 'Our Philosophy';
  const philHeading = philosophy.heading || 'Freedom to Inquire. Confidence to Build.';
  const philQuote =
    philosophy.quote ||
    'Learning becomes meaningful when children have the freedom to question, the confidence to contribute and the opportunity to create.';
  const philP1 =
    philosophy.paragraph_1 ||
    'At St. Andrews World School, Sonipat, we believe that children grow through active participation in their learning. Planting a sapling, presenting an idea, building a model or working towards a shared goal gives students opportunities to think independently and act with purpose.';
  const philP2 =
    philosophy.paragraph_2 ||
    'Our approach to student engagement and experiential learning connects academic understanding with practical experiences. Through exploration, creativity and collaboration, we encourage children to discover their strengths, take ownership of their work and develop the confidence to embrace new challenges.';

  // Principles
  const principles = Array.isArray(sections.principles) && sections.principles.length > 0
    ? sections.principles
    : ['Explore', 'Create', 'Collaborate', 'Reflect', 'Grow'];

  // Grid Intro
  const gridIntro = sections.grid_intro || {};
  const gridKicker = gridIntro.kicker || 'Learning in action';
  const gridHeading = gridIntro.heading || 'Experiences that shape young lives';
  const gridDesc =
    gridIntro.description ||
    'Every activity is an invitation to investigate, express, collaborate and reflect.';

  // Engagements (6 core experiential modules)
  const rawEngagements = Array.isArray(sections.engagements) && sections.engagements.length > 0
    ? sections.engagements
    : DEFAULT_ENGAGEMENTS;

  const engagements = rawEngagements.filter((item) => {
    const t = (item.title || '').toLowerCase();
    return !t.includes('3d lab') &&
           !t.includes('robotics') &&
           !t.includes('project making') &&
           !t.includes('model building') &&
           !t.includes('experiment') &&
           !t.includes('scientific inquiry');
  });

  const gridPart1 = engagements.slice(0, 3);
  const gridPart2 = engagements.slice(3);

  // Feature Split
  const featSplit = sections.feature_split || {};
  const featKicker = featSplit.kicker || 'Learning by doing';
  const featQuote =
    featSplit.quote || 'An idea gains strength when a child can express it with conviction.';
  const featText =
    featSplit.text ||
    'We create space for every student to turn knowledge into action—and action into genuine confidence.';
  const featImage = resolveImage(featSplit.image, '/images/engagement/presentation.webp');

  // Outro CTA
  const outro = sections.outro || {};
  const outroKicker = outro.kicker || 'Beyond achievement';
  const outroHeading = outro.heading || 'Meaningful Experiences. Lasting Capabilities.';
  const outroP1 =
    outro.paragraph_1 ||
    'At St. Andrews World School, Sonipat, student engagement supports an education that extends beyond academic achievement. Every opportunity to investigate, express, collaborate and reflect helps children develop capabilities they can carry into future learning and everyday life.';
  const outroP2 =
    outro.paragraph_2 ||
    'For families seeking a school in Sonipat that values intellectual curiosity, confident communication and personal growth, we invite you to discover our approach to learning.';
  const outroBadge = outro.badge_text || 'Purpose in every experience';
  const outroImage = resolveImage(outro.image, '/images/engagement/nursury.webp');
  const outroBtnText = outro.button_text || 'Discover our school';
  const outroBtnUrl = outro.button_url || '/contact-us';

  return (
    <>
      <SEO
        slug="beyond-classroom"
        title="Beyond Classroom | St. Andrews World School Sonipat"
        description="Discover how St. Andrews World School, Sonipat builds confident, curious learners through active participation, experiential projects and peer collaboration."
        keywords="Beyond Classroom, Student Engagement, Experiential Learning, SAWS Sonipat, Co-curricular Activities Sonipat"
      />

      {/* ─── HERO BANNER ─────────────────────────────────────────── */}
      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        image={bannerImgSrc}
        fallback="/images/page-banners/admission-guidliness.jpg"
      />

      {/* ─── SECTION 1: OUR PHILOSOPHY (ODD: bg-white — 75% on desktop) ─ */}
      <section className="py-14 md:py-20 bg-white border-b border-gray-100">
        <div className="w-full lg:w-[75%] lg:max-w-[75%] mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12 items-start">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                  {philKicker}
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1b3160] leading-tight font-serif">
                  {philHeading}
                </h2>
              </div>
              <div className="border-l-2 md:border-l-4 border-[#d8a85f] pl-6 md:pl-8">
                <blockquote className="text-lg sm:text-xl md:text-2xl leading-relaxed text-[#1b3160] font-serif font-normal italic">
                  "{philQuote}"
                </blockquote>
                <div className="mt-6 space-y-4 text-gray-600 text-[15px] sm:text-base leading-relaxed">
                  <p>{philP1}</p>
                  <p>{philP2}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 2: CORE PRINCIPLES RIBBON (bg-[#1b3160]) ───── */}
      <section className="bg-[#1b3160] text-white py-6 md:py-8 shadow-inner" aria-label="Core principles">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-x-6 gap-y-2 px-5 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] md:justify-between md:px-10">
          {principles.map((pr, idx) => (
            <React.Fragment key={idx}>
              <span className="hover:text-[#d8a85f] transition-colors">{pr}</span>
              {idx < principles.length - 1 && (
                <span className="text-[#d8a85f] font-normal">•</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ─── SECTION 3: EXPERIENCES GRID PART 1 (EVEN: bg-[#f9f8f4]) ─ */}
      <section className="bg-[#f9f8f4] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="mb-12 grid gap-6 md:grid-cols-2 md:items-end">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-2 block">
                  {gridKicker}
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1b3160] font-serif leading-tight">
                  {gridHeading}
                </h2>
              </div>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed justify-self-start md:justify-self-end max-w-md">
                {gridDesc}
              </p>
            </div>
          </Reveal>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gridPart1.map((item, idx) => (
              <Reveal key={item.title || idx} delayMs={idx * 50} className="h-full">
                <article className="group h-full bg-white border border-gray-200/90 hover:border-[#d8a85f] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6 sm:p-8 flex flex-col justify-between overflow-hidden">
                  <div>
                    <div className="mb-6 overflow-hidden rounded-none h-56 sm:h-64 relative bg-gray-100">
                      <img
                        src={resolveImage(item.image)}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading={idx < 2 ? 'eager' : 'lazy'}
                        onError={(e) => {
                          e.target.src = '/images/page-banners/admission-guidliness.jpg';
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-[#1b3160]/10 text-[#1b3160] flex items-center justify-center text-xs font-bold">
                        <i className={`fas ${item.icon || 'fa-star'} text-[11px]`}></i>
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#d8a85f]">
                        {item.subtitle}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#1b3160] font-serif leading-snug group-hover:text-[#d8a85f] transition-colors mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-[15px] sm:text-[16px] leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 4: LEARNING BY DOING (SPLIT FEATURE — bg-[#1b3160]) ─ */}
      <section className="grid bg-[#1b3160] lg:grid-cols-2 overflow-hidden">
        <div className="min-h-[300px] sm:min-h-[380px] lg:min-h-[520px] relative overflow-hidden group">
          <img
            src={featImage}
            alt="Students engaged in hands-on learning"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.target.src = '/images/page-banners/admission-guidliness.jpg';
            }}
          />
        </div>
        <div className="flex items-center px-6 py-14 sm:px-12 lg:px-16 text-white">
          <div className="max-w-xl">
            <p className="mb-4 flex items-center gap-3 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#d8a85f]">
              <span className="h-px w-8 bg-[#d8a85f]"></span>
              <span>{featKicker}</span>
            </p>
            <blockquote className="text-2xl sm:text-3xl md:text-4xl leading-tight font-serif font-bold my-5 text-white">
              "{featQuote}"
            </blockquote>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {featText}
            </p>
          </div>
        </div>
      </section>

      {/* ─── SECTION 5: EXPERIENCES GRID PART 2 (EVEN: bg-[#f9f8f4]) ─ */}
      <section className="bg-[#f9f8f4] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gridPart2.map((item, idx) => (
              <Reveal key={item.title || idx} delayMs={idx * 50} className="h-full">
                <article className="group h-full bg-white border border-gray-200/90 hover:border-[#d8a85f] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6 sm:p-8 flex flex-col justify-between overflow-hidden">
                  <div>
                    <div className="mb-6 overflow-hidden rounded-none h-56 sm:h-64 relative bg-gray-100">
                      <img
                        src={resolveImage(item.image)}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = '/images/page-banners/admission-guidliness.jpg';
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-[#1b3160]/10 text-[#1b3160] flex items-center justify-center text-xs font-bold">
                        <i className={`fas ${item.icon || 'fa-star'} text-[11px]`}></i>
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#d8a85f]">
                        {item.subtitle}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#1b3160] font-serif leading-snug group-hover:text-[#d8a85f] transition-colors mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-[15px] sm:text-[16px] leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 6: BEYOND ACHIEVEMENT (OUTRO CTA — ODD: bg-white) ─ */}
      <section className="bg-white py-16 md:py-24 border-t border-gray-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
              <div className="relative group overflow-hidden">
                <img
                  src={outroImage}
                  alt="Student development beyond the classroom"
                  className="aspect-[4/3] w-full object-cover rounded-none shadow-md transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = '/images/page-banners/admission-guidliness.jpg';
                  }}
                />
                <div className="absolute -bottom-1 right-0 bg-[#d8a85f] px-6 py-3.5 font-bold text-[#1b3160] text-xs sm:text-sm uppercase tracking-wider shadow-lg">
                  {outroBadge}
                </div>
              </div>
              <div className="lg:pl-6">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                  {outroKicker}
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1b3160] font-serif leading-tight mb-6">
                  {outroHeading}
                </h2>
                <div className="space-y-4 text-gray-600 text-[15px] sm:text-base leading-relaxed">
                  <p>{outroP1}</p>
                  <p>{outroP2}</p>
                </div>
                <div className="mt-8">
                  <Link
                    to={outroBtnUrl}
                    className="inline-flex items-center gap-2.5 bg-[#1b3160] text-white hover:bg-[#d8a85f] hover:text-[#1b3160] px-8 py-3.5 text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md group"
                  >
                    <span>{outroBtnText}</span>
                    <i className="fas fa-arrow-right text-[12px] group-hover:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default BeyondClassroomPage;
